import React from 'react';
import PropTypes from 'prop-types';
import { __, sprintf } from '@wordpress/i18n';

// `min="0"` only blocks the native spinner/validity state, not the keyboard
// — a browser still happily accepts "-5" typed directly, which would
// otherwise flow straight into the savings/profit math with no warning
// (see the outcome cell's negative-value styling below). Clamp here so
// every one of this table's numeric fields shares the same floor.
//
// `value` is the *raw* per-field state (possibly null while the user has
// cleared it to type something new) — never the already-defaulted number
// used for calculations. Rendering `value ?? 0` there used to mean
// clearing the field stored an explicit 0, which redisplayed as "0"
// instead of blank; typing a new digit then landed *after* that "0"
// instead of replacing it. `value ?? ''` lets the field go genuinely
// empty, and clearing now reports `null` (not 0) so callers can tell
// "cleared, about to type something new" apart from "actually zero".
const numericInput = ( label, value, onChange, step = '1' ) => (
	<input
		aria-label={ label }
		type="number"
		min="0"
		step={ step }
		value={ value ?? '' }
		onChange={ ( event ) => {
			const raw = event.target.value;

			if ( raw === '' ) {
				onChange( null );
				return;
			}

			const parsed = Number( raw );
			onChange(
				Number.isFinite( parsed ) ? Math.max( 0, parsed ) : null
			);
		} }
	/>
);

// Disambiguates rows that share a name across device groups (e.g. "Single
// crown" exists under both SprintRay Midas and SprintRay Pro 2 on the
// combined ecosystem product) so per-row aria-labels/titles stay unique.
const qualifiedIndicationName = ( indication ) =>
	indication.deviceGroup
		? `${ indication.deviceGroup } — ${ indication.name }`
		: indication.name;

// Reference tool's design-cost "source" pills (AI / SR / My Lab): one click
// sets the whole cell to that source's preset, instead of typing a number.
// Only Midas indications offer AI (free); every indication offers SR (the
// SprintRay design service, at its own per-indication price) and My Lab
// (clears the field for a self-reported figure). Unlike the reference,
// which hides the number input until "My Lab" is picked, ours stays
// visible/editable at all times — typing into it counts as "My Lab" too.
const designSourcePills = ( indication, activeSource, onSelect ) => (
	<div
		className="roi-calculator__design-pills"
		role="group"
		aria-label={ sprintf(
			/* translators: %s: indication name */
			__( 'Design cost source for %s', 'sprintray-roi-calculator' ),
			qualifiedIndicationName( indication )
		) }
	>
		{ indication.hasAiDesignOption && (
			<button
				type="button"
				className={
					'roi-calculator__pill' +
					( activeSource === 'ai' ? ' is-active' : '' )
				}
				aria-pressed={ activeSource === 'ai' }
				title={ __(
					'Free AI-assisted design',
					'sprintray-roi-calculator'
				) }
				onClick={ () => onSelect( indication.id, 'ai' ) }
			>
				{ __( 'AI', 'sprintray-roi-calculator' ) }
			</button>
		) }
		<button
			type="button"
			className={
				'roi-calculator__pill' +
				( activeSource === 'sr' ? ' is-active' : '' )
			}
			aria-pressed={ activeSource === 'sr' }
			title={ __(
				'SprintRay design service',
				'sprintray-roi-calculator'
			) }
			onClick={ () => onSelect( indication.id, 'sr' ) }
		>
			{ __( 'SR', 'sprintray-roi-calculator' ) }
		</button>
		<button
			type="button"
			className={
				'roi-calculator__pill' +
				( activeSource === 'custom' ? ' is-active' : '' )
			}
			aria-pressed={ activeSource === 'custom' }
			title={ __(
				'Design it yourself or via your own lab',
				'sprintray-roi-calculator'
			) }
			onClick={ () => onSelect( indication.id, 'custom' ) }
		>
			{ __( 'My Lab', 'sprintray-roi-calculator' ) }
		</button>
	</div>
);

// Reference tool's colored section header ("SprintRay Midas" / "SprintRay
// Pro 2"), shown as a full-width row wherever the indication list crosses
// into a new device group — e.g. on the combined ecosystem product, which
// has both. `materialAnnotation` (e.g. "· active material: Ceramic
// Crown") is only ever shown on the first group, since only one device
// (Midas) has a material-selection step to begin with.
const deviceGroupHeader = ( groupName, materialAnnotation, colSpan ) => (
	<tr key={ `group-${ groupName }` } className="roi-calculator__group-row">
		<td colSpan={ colSpan }>
			{ /* The flex layout lives on this inner div, not the <td> itself
			   — a <td> switched to display:flex loses its colspan-derived
			   width in some browsers and collapses to a single column. */ }
			<div className="roi-calculator__group-content">
				<svg
					width="14"
					height="14"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden="true"
				>
					<path d="M6 9V3h12v6" />
					<rect x="3" y="9" width="18" height="9" rx="2" />
					<path d="M6 14h12v7H6z" />
				</svg>
				{ groupName }
				{ materialAnnotation && (
					<span className="roi-calculator__group-annotation">
						{ materialAnnotation }
					</span>
				) }
			</div>
		</td>
	</tr>
);

// One combined table for enabling/disabling indications AND editing their
// per-case values — mirrors the reference tool's single "Ihre Indikationen"
// / "Ihre Produktionen" table (click any cell to edit, toggle a row off,
// reset it back to defaults) rather than splitting that into separate
// steps. Shared by both calculator variants: Praxis passes a `designCost`
// prop and gets a "Design Cost" column + "Savings/Month" outcome; Labor
// omits it and gets "Gross Profit/Month" instead.
const IndicationsTable = ( {
	indications,
	activeMaterialName,
	enabledMap,
	showDescriptions = false,
	casesPerMonth,
	priceByIndication,
	materialCost,
	designCost,
	designSource,
	priceColumnLabel,
	outcomeColumnLabel,
	computeOutcome,
	onToggle,
	onChangeCases,
	onChangePrice,
	onChangeMaterialCost,
	onChangeDesignCost,
	onSelectDesignSource,
	onReset,
	currency,
} ) => {
	const hasDesignCost = !! designCost && !! onChangeDesignCost;
	// toggle, indication, price, cases, material, outcome, reset (+ design).
	const columnCount = hasDesignCost ? 8 : 7;
	let lastRenderedGroup = null;
	let isFirstGroup = true;

	return (
		<div className="roi-calculator__step roi-calculator__step--indications">
			<h3>{ __( 'Your indications', 'sprintray-roi-calculator' ) }</h3>
			<p className="roi-calculator__step-hint">
				{ __(
					'Click any cell to edit it — everything recalculates live. Turn off a row that does not apply.',
					'sprintray-roi-calculator'
				) }
			</p>
			<div className="roi-calculator__table-scroll">
				<table className="roi-calculator__input-table">
					<thead>
						<tr>
							<th className="roi-calculator__col-toggle" />
							<th className="roi-calculator__col-name">
								{ __(
									'Indication',
									'sprintray-roi-calculator'
								) }
							</th>
							<th>{ priceColumnLabel }</th>
							<th>
								{ __(
									'Cases / Month',
									'sprintray-roi-calculator'
								) }
							</th>
							<th>
								{ __(
									'Material Cost',
									'sprintray-roi-calculator'
								) }
							</th>
							{ hasDesignCost && (
								<th>
									{ __(
										'Design Cost',
										'sprintray-roi-calculator'
									) }
								</th>
							) }
							<th>
								{ outcomeColumnLabel } ({ currency })
							</th>
							<th className="roi-calculator__col-reset" />
						</tr>
					</thead>
					<tbody>
						{ indications.flatMap( ( indication ) => {
							const isEnabled = !! enabledMap[ indication.id ];
							// Resolved values (state, falling back to this
							// indication's own defaults, then 0) — used for
							// the live outcome preview below, which should
							// keep showing a sensible number even mid-edit
							// rather than collapsing to 0 the instant a
							// field goes empty.
							const cases =
								casesPerMonth[ indication.id ] ??
								indication.defaultCasesPerMonth ??
								0;
							const price =
								priceByIndication[ indication.id ] ??
								indication.defaultLabOutsourcingCost ??
								0;
							const material =
								materialCost[ indication.id ] ??
								indication.defaultMaterialCost ??
								0;
							const design = hasDesignCost
								? designCost[ indication.id ] ??
								  indication.defaultDesignCost ??
								  0
								: 0;
							// Raw (possibly null) per-field state, for what
							// the inputs themselves display — see
							// numericInput's own comment above for why this
							// has to be separate from the resolved values.
							const rawCases = casesPerMonth[ indication.id ];
							const rawPrice = priceByIndication[ indication.id ];
							const rawMaterial = materialCost[ indication.id ];
							const rawDesign = hasDesignCost
								? designCost[ indication.id ]
								: 0;
							const activeDesignSource =
								designSource?.[ indication.id ];
							const outcome = computeOutcome( {
								price,
								material,
								design,
								cases,
							} );

							let groupHeader = null;
							if (
								indication.deviceGroup &&
								indication.deviceGroup !== lastRenderedGroup
							) {
								groupHeader = deviceGroupHeader(
									indication.deviceGroup,
									isFirstGroup && activeMaterialName
										? sprintf(
												/* translators: %s: material name */
												__(
													'· active material: %s',
													'sprintray-roi-calculator'
												),
												activeMaterialName
										  )
										: null,
									columnCount
								);
								lastRenderedGroup = indication.deviceGroup;
								isFirstGroup = false;
							}

							const qualifiedName =
								qualifiedIndicationName( indication );
							const isNegativeOutcome = outcome < 0;

							const row = (
								<tr
									key={ indication.id }
									className={
										isEnabled
											? ''
											: 'roi-calculator__row--disabled'
									}
								>
									<td className="roi-calculator__col-toggle">
										<button
											type="button"
											className={
												'roi-calculator__toggle' +
												( isEnabled
													? ' is-enabled'
													: '' )
											}
											aria-pressed={ isEnabled }
											aria-label={
												isEnabled
													? sprintf(
															/* translators: %s: indication name */
															__(
																'Disable %s',
																'sprintray-roi-calculator'
															),
															qualifiedName
													  )
													: sprintf(
															/* translators: %s: indication name */
															__(
																'Enable %s',
																'sprintray-roi-calculator'
															),
															qualifiedName
													  )
											}
											title={
												isEnabled
													? __(
															'Disable row',
															'sprintray-roi-calculator'
													  )
													: __(
															'Enable row',
															'sprintray-roi-calculator'
													  )
											}
											onClick={ () =>
												onToggle( indication.id )
											}
										>
											{ isEnabled ? '✓' : '' }
										</button>
									</td>
									<td className="roi-calculator__col-name">
										<div className="roi-calculator__indication-name">
											{ indication.name }
										</div>
										{ showDescriptions &&
											indication.description && (
												<div className="roi-calculator__indication-description">
													{ indication.description }
												</div>
											) }
									</td>
									<td>
										{ numericInput(
											`${ priceColumnLabel } ${ qualifiedName }`,
											rawPrice,
											( value ) =>
												onChangePrice(
													indication.id,
													value
												)
										) }
									</td>
									<td>
										{ numericInput(
											sprintf(
												/* translators: %s: indication name */
												__(
													'Cases per month for %s',
													'sprintray-roi-calculator'
												),
												qualifiedName
											),
											rawCases,
											( value ) =>
												onChangeCases(
													indication.id,
													value
												)
										) }
									</td>
									<td>
										{ numericInput(
											sprintf(
												/* translators: %s: indication name */
												__(
													'Material cost for %s',
													'sprintray-roi-calculator'
												),
												qualifiedName
											),
											rawMaterial,
											( value ) =>
												onChangeMaterialCost(
													indication.id,
													value
												)
										) }
									</td>
									{ hasDesignCost && (
										<td className="roi-calculator__col-design">
											<div className="roi-calculator__design-cell">
												{ designSourcePills(
													indication,
													activeDesignSource,
													onSelectDesignSource
												) }
												{ numericInput(
													sprintf(
														/* translators: %s: indication name */
														__(
															'Design cost for %s',
															'sprintray-roi-calculator'
														),
														qualifiedName
													),
													rawDesign,
													( value ) =>
														onChangeDesignCost(
															indication.id,
															value
														)
												) }
											</div>
										</td>
									) }
									<td
										className={
											'roi-calculator__outcome' +
											( isNegativeOutcome
												? ' roi-calculator__outcome--negative'
												: '' )
										}
									>
										{ isNegativeOutcome && (
											<span aria-hidden="true">⚠ </span>
										) }
										{ outcome.toFixed( 2 ) }
										{ isNegativeOutcome && (
											<span className="roi-calculator__visually-hidden">
												{ __(
													'(negative — check this row’s values)',
													'sprintray-roi-calculator'
												) }
											</span>
										) }
									</td>
									<td>
										<button
											type="button"
											className="roi-calculator__reset"
											aria-label={ sprintf(
												/* translators: %s: indication name */
												__(
													'Reset %s to defaults',
													'sprintray-roi-calculator'
												),
												qualifiedName
											) }
											title={ __(
												'Reset to defaults',
												'sprintray-roi-calculator'
											) }
											onClick={ () =>
												onReset( indication.id )
											}
										>
											↺
										</button>
									</td>
								</tr>
							);

							return groupHeader ? [ groupHeader, row ] : [ row ];
						} ) }
					</tbody>
				</table>
			</div>
		</div>
	);
};

IndicationsTable.propTypes = {
	indications: PropTypes.array.isRequired,
	activeMaterialName: PropTypes.string,
	enabledMap: PropTypes.object.isRequired,
	// Calculator setting `show_indication_descriptions` (off by default).
	showDescriptions: PropTypes.bool,
	casesPerMonth: PropTypes.object.isRequired,
	priceByIndication: PropTypes.object.isRequired,
	materialCost: PropTypes.object.isRequired,
	designCost: PropTypes.object,
	designSource: PropTypes.object,
	priceColumnLabel: PropTypes.string.isRequired,
	outcomeColumnLabel: PropTypes.string.isRequired,
	computeOutcome: PropTypes.func.isRequired,
	onToggle: PropTypes.func.isRequired,
	onChangeCases: PropTypes.func.isRequired,
	onChangePrice: PropTypes.func.isRequired,
	onChangeMaterialCost: PropTypes.func.isRequired,
	onChangeDesignCost: PropTypes.func,
	onSelectDesignSource: PropTypes.func,
	onReset: PropTypes.func.isRequired,
	currency: PropTypes.string,
};

export default IndicationsTable;
